/**
 * First-/last-touch attribution for the Shopify storefront.
 *
 * The Next storefront captured this in middleware, because purchases happened on
 * a different host and the cross-domain hop lost the original source. That hop is
 * gone — storefront and checkout are one domain now — but Shopify still does not
 * put UTMs on the order itself, which is what Rooted HQ reads. So we capture the
 * touch in the browser and stamp it onto the cart, using the same attribute keys
 * the Next app wrote so nothing downstream has to change.
 *
 * Precedence matches lib/attribution.ts exactly:
 *   explicit UTMs > gclid > paid Meta click ids > external referrer >
 *   organic Meta click ids > direct.
 */
(() => {
  const FIRST = 'rooted_first_touch';
  const LAST = 'rooted_last_touch';
  const INTERNAL = ['rootedcandle.com', 'myshopify.com', 'localhost', '127.0.0.1'];
  const SEARCH = [[/(^|\.)google\./, 'google'], [/(^|\.)bing\./, 'bing'], [/(^|\.)duckduckgo\./, 'duckduckgo'], [/(^|\.)yahoo\./, 'yahoo'], [/(^|\.)ecosia\./, 'ecosia']];
  const SOCIAL = [[/(^|\.)instagram\.com$/, 'instagram'], [/(^|\.)facebook\.com$/, 'facebook'], [/(^|\.)fb\.(com|me)$/, 'facebook'], [/(^|\.)pinterest\./, 'pinterest'], [/(^|\.)t\.co$/, 'twitter'], [/(^|\.)(twitter|x)\.com$/, 'twitter'], [/(^|\.)tiktok\.com$/, 'tiktok'], [/(^|\.)linkedin\.com$/, 'linkedin'], [/(^|\.)youtube\.com$/, 'youtube']];

  const clean = (v, max = 255) => { const t = String(v || '').trim(); return t ? t.slice(0, max) : undefined; };
  const host = url => { try { return new URL(url).hostname.toLowerCase(); } catch { return ''; } };
  const internal = h => INTERNAL.some(s => h === s || h.endsWith('.' + s));
  const read = key => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };
  const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ } };

  function classify(h) {
    for (const [re, source] of SEARCH) if (re.test(h)) return { source, medium: 'organic' };
    for (const [re, source] of SOCIAL) if (re.test(h)) return { source, medium: 'social' };
    return { source: h, medium: 'referral' };
  }

  function parseTouch() {
    const p = new URLSearchParams(location.search);
    const utmSource = clean(p.get('utm_source')), utmMedium = clean(p.get('utm_medium'));
    const fbclid = clean(p.get('fbclid')), gclid = clean(p.get('gclid'));
    const metaCampaignId = clean(p.get('campaign_id')), metaAdsetId = clean(p.get('adset_id')), metaAdId = clean(p.get('ad_id'));
    const refHost = host(document.referrer);
    const externalRef = refHost && !internal(refHost) ? refHost : '';
    let source, medium;
    if (utmSource) { source = utmSource; medium = utmMedium || '(none)'; }
    else if (gclid) { source = 'google'; medium = 'cpc'; }
    else if (fbclid && (metaCampaignId || metaAdsetId || metaAdId)) { source = 'meta'; medium = 'paid_social'; }
    else if (externalRef) { ({ source, medium } = classify(externalRef)); }
    else if (fbclid) { source = 'facebook'; medium = 'social'; }
    else { source = '(direct)'; medium = '(none)'; }
    return {
      source, medium,
      campaign: clean(p.get('utm_campaign')), term: clean(p.get('utm_term')), content: clean(p.get('utm_content')),
      landingPage: clean(location.pathname), referrer: clean(externalRef ? document.referrer : ''),
      fbclid, gclid, metaCampaignId, metaAdsetId, metaAdId,
      date: new Date().toISOString().slice(0, 10),
    };
  }

  /** A touch worth recording: anything that is not a bare direct hit. */
  const meaningful = t => t.source !== '(direct)';
  const identity = t => t && [t.source, t.medium, t.campaign, t.term, t.content, t.landingPage, t.referrer, t.fbclid, t.gclid, t.metaCampaignId, t.metaAdsetId, t.metaAdId, t.date].map(v => v || '').join('');

  const touch = parseTouch();
  const first = read(FIRST);
  // First touch is written once and never overwritten; a later direct visit must
  // not erase the campaign that originally brought someone here.
  if (!first && meaningful(touch)) write(FIRST, touch);
  if (meaningful(touch)) write(LAST, touch);

  function attributesFor(prefix, t) {
    const out = {};
    out[`${prefix} source`] = `${t.source} / ${t.medium}`;
    if (t.campaign) out[`${prefix} campaign`] = t.campaign;
    if (t.term) out[`${prefix} term`] = t.term;
    if (t.content) out[`${prefix} content`] = t.content;
    if (t.landingPage) out[`${prefix} landing page`] = t.landingPage;
    if (t.referrer) out[`${prefix} referrer`] = t.referrer;
    if (t.fbclid) out[`${prefix} fbclid`] = t.fbclid;
    if (t.gclid) out[`${prefix} gclid`] = t.gclid;
    if (t.metaCampaignId) out[`${prefix} Meta campaign id`] = t.metaCampaignId;
    if (t.metaAdsetId) out[`${prefix} Meta adset id`] = t.metaAdsetId;
    if (t.metaAdId) out[`${prefix} Meta ad id`] = t.metaAdId;
    out[`${prefix} date`] = t.date;
    return out;
  }

  /** The attributes to stamp on the cart, matching what the Next app wrote. */
  function cartAttributes() {
    const f = read(FIRST), l = read(LAST);
    if (!f && !l) return null;
    let out = {};
    if (f) out = attributesFor('First-touch', f);
    // Last-touch only when it differs, to keep single-session orders uncluttered.
    if (l && identity(l) !== identity(f)) out = { ...out, ...attributesFor('Last-touch', l) };
    return Object.keys(out).length ? out : null;
  }

  window.RootedAttribution = {
    cartAttributes,
    // Written on the first cart mutation; Shopify carries attributes to the order.
    async stamp() {
      const attributes = cartAttributes();
      if (!attributes) return;
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
          signal: AbortSignal.timeout(8000),
        });
      } catch { /* never block a cart action on attribution */ }
    },
  };
})();
